
import { Companies, EquipmentFitments } from "@/api";
import {
  EquipmentFitment,
  EquipmentFitmentLine
} from "@/api/equipmentFitments";
import PageTitle from "@/components/common/PageTitle.vue";
import EquipmentFitmentItemsTable from "@/components/equipmentFitment/EquipmentFitmentItemsTable.vue";
import EquipmentFitmentViewDialog from "@/components/equipmentFitment/EquipmentFitmentViewDialog.vue";
import moment from "moment";
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";

const Auth = namespace("auth");

/** A row of the list, as the server answers it. */
type Fitment = EquipmentFitment & { id: number };

const emptyForm = () => ({
  companyId: null as string | null,
  hansaAcc: "",
  vehicleType: null as string | null,
  vehicleCategory: null as string | null,
  vehicleMake: null as string | null,
  registrationNumber: "",
  checkedBy: "",
  cashSale: false,
  scheduledDate: null as string | null,
  scheduledTime: "",
  arrivalTime: "",
  departureTime: "",
  notes: "",
  lines: [] as EquipmentFitmentLine[]
});

@Component({
  components: {
    PageTitle,
    EquipmentFitmentItemsTable,
    EquipmentFitmentViewDialog
  }
})
export default class EquipmentFitmentPage extends Vue {
  @Auth.Getter("getUser") getUser!: any;

  pageHeaders = [{ icon: "mdi-wrench-outline", header: "Equipment Fitment" }];

  headers = [
    { text: "Date", value: "date", align: "start", width: "110px" },
    {
      text: "Scheduled",
      value: "scheduledAt",
      align: "start",
      width: "150px"
    },
    { text: "User created", value: "createdBy", align: "start" },
    { text: "Customer name", value: "customerName", align: "start" },
    { text: "HANSA acc", value: "hansaAcc", align: "start", width: "120px" },
    {
      text: "Reg number",
      value: "registrationNumber",
      align: "start",
      width: "130px"
    },
    { text: "Vehicle category", value: "vehicleType", align: "start" },
    { text: "Status", value: "status", align: "start", width: "120px" },
    {
      text: "Actions",
      value: "actions",
      align: "start",
      sortable: false,
      width: "200px"
    }
  ];

  vehicleTypes: string[] = [];
  vehicleTypeOptions: Array<{ id: number; name: string }> = [];
  /** The QA team, for the Checked by picker. */
  qaUsers: Array<{ id: number; name: string }> = [];
  vehicleCategories: string[] = [];
  vehicleCategoryOptions: Array<{ id: number; name: string }> = [];
  saving = false;
  loading = false;

  vehicleMakes: string[] = [];

  fitments: Fitment[] = [];
  companies: any[] = [];
  /** Which tab is open; the server does the splitting by status. */
  tab = 0;
  tabNames = ["Pending", "Approved", "Finalized"];
  tabKeys: Array<"pending" | "approved" | "finalized"> = [
    "pending",
    "approved",
    "finalized"
  ];
  filters: {
    customer: string | null;
    hansaAcc: string | null;
    vehicleCategoryId: number | null;
    date: string | null;
  } = {
    customer: null,
    hansaAcc: null,
    vehicleCategoryId: null,
    date: null
  };
  allVehicleCategories: Array<{ id: number; name: string }> = [];
  dateMenu = false;
  private filterTimer: number | null = null;
  showDialog = false;
  readOnly = false;
  editingId: number | null = null;
  showView = false;
  viewingId: number | null = null;
  form = emptyForm();
  scheduledDateMenu = false;

  get selectedVehicleTypeId(): number | null {
    const type = this.vehicleTypeOptions.find(
      option => option.name === this.form.vehicleType
    );
    return type ? type.id : null;
  }

  async created(): Promise<void> {
    try {
      this.companies = await Companies.listWithoutPagination();
    } catch (e) {
      this.companies = [];
    }

    try {
      this.vehicleTypeOptions = await EquipmentFitments.vehicleTypes();
      this.vehicleTypes = this.vehicleTypeOptions.map(type => type.name);
    } catch (e) {
      this.vehicleTypeOptions = [];
      this.vehicleTypes = [];
    }

    try {
      this.qaUsers = await EquipmentFitments.qaUsers();
    } catch (e) {
      this.qaUsers = [];
    }

    // Every category, for the list's filter; the form asks per type.
    try {
      this.allVehicleCategories = await EquipmentFitments.vehicleCategories();
    } catch (e) {
      this.allVehicleCategories = [];
    }

    await this.loadFitments();
  }

  // Typing in a filter box should not fire a request per keystroke.
  @Watch("filters.customer")
  @Watch("filters.hansaAcc")
  onTypedFilterChange(): void {
    if (this.filterTimer) window.clearTimeout(this.filterTimer);
    this.filterTimer = window.setTimeout(() => this.loadFitments(), 350);
  }

  @Watch("filters.vehicleCategoryId")
  @Watch("filters.date")
  async onPickedFilterChange(): Promise<void> {
    await this.loadFitments();
  }

  formatDate(value: string): string {
    return moment(value).format("DD/MM/YYYY");
  }

  /**
   * The kind of vehicle decides the categories and the makes, so changing it
   * reloads both. A category or make that the new kind does not offer is
   * cleared; one it does offer is kept, so opening a saved fitment keeps its
   * own category and make rather than losing them to this reload.
   */
  @Watch("form.vehicleType")
  async onVehicleTypeChange(name: string | null): Promise<void> {
    this.vehicleCategories = [];
    this.vehicleCategoryOptions = [];
    this.vehicleMakes = [];

    const type = this.vehicleTypeOptions.find(option => option.name === name);
    if (!type) {
      this.form.vehicleCategory = null;
      this.form.vehicleMake = null;
      return;
    }

    try {
      const [categories, makes] = await Promise.all([
        EquipmentFitments.vehicleCategories(type.id),
        EquipmentFitments.vehicleMakes(type.id)
      ]);
      // Guard against a slower reply for a type that is no longer selected.
      if (this.form.vehicleType !== name) return;
      this.vehicleCategoryOptions = categories;
      this.vehicleCategories = categories.map(row => row.name);
      this.vehicleMakes = makes.map(row => row.name);
      if (!this.vehicleCategories.includes(this.form.vehicleCategory || "")) {
        this.form.vehicleCategory = null;
      }
      if (!this.vehicleMakes.includes(this.form.vehicleMake || "")) {
        this.form.vehicleMake = null;
      }
    } catch (e) {
      await this.$error(e);
    }
  }

  get dialogTitle(): string {
    if (this.readOnly) return "Equipment fitment";
    return this.editingId ? "Edit equipment fitment" : "New equipment fitment";
  }

  /** The server sends the date and time apart; the list shows them as one. */
  scheduledAtOf(row: Fitment): string {
    if (!row.scheduledDate) return "-";
    const date = moment(row.scheduledDate).format("DD/MM/YYYY");
    return row.scheduledTime ? `${date} ${row.scheduledTime}` : date;
  }

  /**
   * The server decides the word — a pending ticket whose proforma is out
   * reads In Progress, a closed one reads Finalized — and falls back to the
   * stored status for a row answered before that was so.
   */
  statusLabel(row: Fitment): string {
    if (row.displayStatus) return row.displayStatus;
    return row.status === "Closed" ? "Finalized" : row.status || "";
  }

  setScheduledDate(value: string): void {
    this.form.scheduledDate = value;
    this.scheduledDateMenu = false;
  }

  /** The scheduled date on its own, as the picker's field shows it. */
  get scheduledDateText(): string {
    return this.form.scheduledDate
      ? moment(this.form.scheduledDate).format("DD/MM/YYYY")
      : "";
  }

  get scheduledAt(): string {
    if (!this.form.scheduledDate) return "";
    return this.form.scheduledTime
      ? `${moment(this.form.scheduledDate).format("DD/MM/YYYY")} ${
          this.form.scheduledTime
        }`
      : moment(this.form.scheduledDate).format("DD/MM/YYYY");
  }

  async onCompanyChange(companyId: string): Promise<void> {
    const company = this.companies.find((row: any) => row.id === companyId);
    this.form.hansaAcc =
      (company && (company.hansaAcc || company.hansa_acc)) || "";
  }

  openCreate(): void {
    this.readOnly = false;
    this.editingId = null;
    this.form = emptyForm();
    this.showDialog = true;
  }

  /** The general details only; the items are revised on the ticket. */
  openEdit(row: Fitment): void {
    this.readOnly = false;
    this.editingId = row.id;
    this.form = {
      companyId: row.companyId,
      hansaAcc: row.hansaAcc || "",
      vehicleType: row.vehicleType || null,
      vehicleCategory: row.vehicleCategory || null,
      vehicleMake: row.vehicleMake,
      registrationNumber: row.registrationNumber || "",
      checkedBy: row.checkedBy || "",
      cashSale: Boolean(row.cashSale),
      scheduledDate: row.scheduledDate,
      scheduledTime: row.scheduledTime || "",
      arrivalTime: row.arrivalTime || "",
      departureTime: row.departureTime || "",
      notes: row.notes || "",
      lines: []
    };
    this.showDialog = true;
  }

  openView(row: Fitment): void {
    this.viewingId = row.id;
    this.showView = true;
  }

  /**
   * A fitment is a record now, so who raised it survives a reload. The server
   * recomputes the total from the lines, so what it answers with is what the
   * list shows. An edit sends no lines, and the server leaves them alone.
   */
  async save(): Promise<void> {
    this.saving = true;
    try {
      const payload: Partial<EquipmentFitment> = {
        companyId: this.form.companyId,
        hansaAcc: this.form.hansaAcc,
        registrationNumber: this.form.registrationNumber,
        vehicleTypeId: this.selectedVehicleTypeId,
        vehicleCategoryId: this.categoryIdFor(this.form.vehicleCategory),
        vehicleMake: this.form.vehicleMake,
        checkedBy: this.form.checkedBy,
        cashSale: this.form.cashSale,
        scheduledDate: this.form.scheduledDate,
        scheduledTime: this.form.scheduledTime,
        arrivalTime: this.form.arrivalTime,
        departureTime: this.form.departureTime,
        notes: this.form.notes
      };

      if (this.editingId) {
        await EquipmentFitments.update(this.editingId, payload);
      } else {
        await EquipmentFitments.create({
          ...payload,
          lines: this.form.lines
        } as EquipmentFitment);
      }

      this.showDialog = false;
      await this.loadFitments();
      await this.$success("<strong>Success!</strong> Equipment fitment saved.");
    } catch (e) {
      await this.$error(e);
    } finally {
      this.saving = false;
    }
  }

  private categoryIdFor(name: string | null): number | null {
    const found = this.vehicleCategoryOptions.find(row => row.name === name);
    return found ? found.id : null;
  }

  async loadFitments(): Promise<void> {
    this.loading = true;
    try {
      this.fitments = (await EquipmentFitments.list({
        tab: this.tabKeys[this.tab] || "pending",
        customer: this.filters.customer || null,
        hansaAcc: this.filters.hansaAcc || null,
        vehicleCategoryId: this.filters.vehicleCategoryId || null,
        date: this.filters.date || null
      })) as any[];
    } catch (e) {
      await this.$error(e);
    } finally {
      this.loading = false;
    }
  }

  async confirmDelete(row: Fitment): Promise<void> {
    const answer = await this.$dialog.error({
      text: `Delete the fitment for "${row.registrationNumber ||
        row.customerName}"?`,
      title: "Warning",
      persistent: false,
      actions: { No: { text: "No" }, Yes: { color: "red", text: "Yes I do" } }
    });
    if (answer !== "Yes") return;
    try {
      await EquipmentFitments.remove(row.id as number);
      await this.loadFitments();
    } catch (e) {
      await this.$error(e);
    }
  }
}
